import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Database, HardDrive, Clock, AlertCircle, CheckCircle2, XCircle, Settings, RefreshCw, TrendingUp, TrendingDown, Bell, Filter, Search, Eye, EyeOff, Edit, Save, Plus, Trash2, Info, HelpCircle, ExternalLink, Copy, Globe, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Zap, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Users, Activity, BarChart3, PieChart, LineChart, ScrollText
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const BackupRecovery = () => {
  const [backupMetrics, setBackupMetrics] = useState(null);
  const [backupJobs, setBackupJobs] = useState([]);
  const [recoveryProcedures, setRecoveryProcedures] = useState([]);
  const [backupConfig, setBackupConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const loadBackupData = useCallback(async () => {
    try {
      setLoading(true);
      const [metricsResponse, jobsResponse, proceduresResponse, configResponse] = await Promise.all([
        AdminService.getBackupMetrics(selectedTimeRange),
        AdminService.getBackupJobs(),
        AdminService.getRecoveryProcedures(),
        AdminService.getBackupConfig()
      ]);

      if (metricsResponse.success) setBackupMetrics(metricsResponse.data);
      if (jobsResponse.success) setBackupJobs(jobsResponse.data);
      if (proceduresResponse.success) setRecoveryProcedures(proceduresResponse.data);
      if (configResponse.success) setBackupConfig(configResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange]);

  useEffect(() => {
    loadBackupData();
  }, [loadBackupData]);

  const handleStartBackup = async (jobId) => {
    try {
      const response = await AdminService.startBackupJob(jobId);
      if (response.success) {
        await loadBackupData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStopBackup = async (jobId) => {
    try {
      const response = await AdminService.stopBackupJob(jobId);
      if (response.success) {
        await loadBackupData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRunRecovery = async (procedureId) => {
    try {
      const response = await AdminService.runRecoveryProcedure(procedureId);
      if (response.success) {
        await loadBackupData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateConfig = async (config) => {
    try {
      const response = await AdminService.updateBackupConfig(config);
      if (response.success) {
        setBackupConfig(response.data);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredJobs = backupJobs.filter(job => {
    const matchesSearch = job.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || job.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'running': return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'scheduled': return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading backup data</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backup & Recovery</h1>
          <p className="text-gray-600">Manage backup operations and recovery procedures</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button
            onClick={loadBackupData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: LayoutDashboard },
            { id: 'backups', name: 'Backup Jobs', icon: Database },
            { id: 'recovery', name: 'Recovery', icon: HardDrive },
            { id: 'config', name: 'Configuration', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && backupMetrics && (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {backupMetrics.overviewMetrics.map((metric, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${
                    metric.status === 'healthy' ? 'bg-green-100' :
                    metric.status === 'warning' ? 'bg-yellow-100' :
                    'bg-red-100'
                  }`}>
                    {metric.status === 'healthy' ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : metric.status === 'warning' ? (
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  {metric.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Backup Activity</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {backupMetrics.recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {getStatusIcon(activity.status)}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{activity.jobName}</p>
                        <p className="text-sm text-gray-600">{activity.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">{activity.timestamp}</p>
                      <p className="text-sm text-gray-600">{activity.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup Jobs Tab */}
      {activeTab === 'backups' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search backup jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="failed">Failed</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>

          {/* Backup Jobs List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Backup Jobs</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Run</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{job.name}</div>
                          <div className="text-sm text-gray-600">{job.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {getStatusIcon(job.status)}
                          <span className="ml-1">{job.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.schedule}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.lastRun}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.size}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {job.status === 'running' ? (
                            <button
                              onClick={() => handleStopBackup(job.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Stop
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStartBackup(job.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Start
                            </button>
                          )}
                          <button className="text-gray-600 hover:text-gray-900">
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Tab */}
      {activeTab === 'recovery' && (
        <div className="space-y-6">
          {/* Recovery Procedures */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recovery Procedures</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recoveryProcedures.map((procedure) => (
                  <div key={procedure.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-medium text-gray-900">{procedure.name}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        procedure.status === 'ready' ? 'bg-green-100 text-green-800' :
                        procedure.status === 'testing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {procedure.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{procedure.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        <p>Last Tested: {procedure.lastTested}</p>
                        <p>Recovery Time: {procedure.recoveryTime}</p>
                      </div>
                      <button
                        onClick={() => handleRunRecovery(procedure.id)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                      >
                        Run Recovery
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && backupConfig && (
        <div className="space-y-6">
          {/* Configuration Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Backup Configuration</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Backup Retention (Days)</label>
                  <input
                    type="number"
                    value={backupConfig.retentionDays}
                    onChange={(e) => setBackupConfig({...backupConfig, retentionDays: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Compression Level</label>
                  <select
                    value={backupConfig.compressionLevel}
                    onChange={(e) => setBackupConfig({...backupConfig, compressionLevel: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Encryption</label>
                  <select
                    value={backupConfig.encryption}
                    onChange={(e) => setBackupConfig({...backupConfig, encryption: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="aes128">AES-128</option>
                    <option value="aes256">AES-256</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Storage Location</label>
                  <input
                    type="text"
                    value={backupConfig.storageLocation}
                    onChange={(e) => setBackupConfig({...backupConfig, storageLocation: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => handleUpdateConfig(backupConfig)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupRecovery;